/**
 * ORGANIZATION REVIEW DETAIL PAGE
 *
 * Platform Team: View organization details and approve/reject
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { createClient } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/status-badge';
import {
  Building2,
  MapPin,
  Mail,
  FileText,
  Image as ImageIcon,
} from 'lucide-react';
import { ReviewActions } from '@/components/review-actions';
import type { Organization } from '@/types/database';

interface LocationData {
  id: string;
  name: string;
  description?: string;
  logo_url?: string | null;
}

type OrganizationWithLocations = Organization & {
  locations: LocationData[];
};

function ReviewDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [organization, setOrganization] = useState<OrganizationWithLocations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'PLATFORM_TEAM' && user.role !== 'PLATFORM_ADMIN' && user.role !== 'SYSTEM_ADMIN')) {
    navigate('/unauthorized');
    return null;
  }

  // Data fetching
  useEffect(() => {
    const fetchOrganization = async () => {
      if (!orgId) {
        navigate('/review');
        return;
      }

      try {
        setLoading(true);
        const supabase = createClient();

        // Get organization from onboarding_queue
        const { data: queueData, error: err } = (await supabase
          .from('onboarding_queue')
          .select('*')
          .eq('id', orgId)
          .single()) as { data: any; error: any };

        // Return to list if organization not found
        if (err || !queueData) {
          navigate('/review');
          return;
        }

        // Get locations from onboarding_locations_queue
        const { data: locationsData, error: locationsError } = (await supabase
          .from('onboarding_locations_queue')
          .select('*')
          .eq('onboarding_queue_id', orgId)) as { data: any[]; error: any };

        if (locationsError) throw locationsError;

        // Map locations data
        const locationsArray = locationsData || [];

        const mappedOrganization: OrganizationWithLocations = {
          id: queueData.id,
          name: queueData.organization_name,
          slug: '', // Not used, keeping for type compatibility
          description: queueData.organization_description,
          logo_url: queueData.organization_logo || null,
          primary_contact_name: queueData.primary_contact_name || null,
          primary_contact_email: queueData.primary_contact_email || null,
          primary_contact_phone: null, // Not available in onboarding_queue
          status: queueData.status as Organization['status'],
          reviewed_by: null,
          reviewed_at: null,
          review_notes: queueData.review_notes || null,
          rejection_reason: queueData.rejection_reason || null,
          external_org_id: null,
          payment_link: null,
          payment_received_at: null,
          metadata: {},
          created_at: queueData.created_at,
          updated_at: queueData.updated_at,
          activated_at: null,
          locations: locationsArray.map((loc: any) => ({
            id: loc.id || `${queueData.id}-${locationsArray.indexOf(loc)}`, // Generate ID if not present
            name: loc.location_name,
            description: loc.location_description || null,
            logo_url: loc.location_logo || null,
          })),
        };

        setOrganization(mappedOrganization);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [orgId, navigate]);

  // Render states
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-gray-100 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading organization...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400">Error loading organization</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100'>
            {organization.name}
          </h1>
          <p className='text-gray-600 dark:text-gray-400 mt-1'>
            Review organization for onboarding approval
          </p>
        </div>
        <StatusBadge status={organization.status} />
      </div>

      {/* Organization Details */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Building2 className='w-5 h-5' />
              Organization Information
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                Organization Name
              </label>
              <p className='text-gray-900 dark:text-gray-100'>
                {organization.name}
              </p>
            </div>

            {organization.description && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Description
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  {organization.description}
                </p>
              </div>
            )}

            <div>
              <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                Created
              </label>
              <p className='text-gray-900 dark:text-gray-100'>
                {formatDate(organization.created_at)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <Mail className='w-5 h-5' />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            {organization.primary_contact_name && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Primary Contact
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  {organization.primary_contact_name}
                </p>
              </div>
            )}

            {organization.primary_contact_email && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Email
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  <a
                    href={`mailto:${organization.primary_contact_email}`}
                    className='text-primary hover:underline'
                  >
                    {organization.primary_contact_email}
                  </a>
                </p>
              </div>
            )}

            {organization.primary_contact_phone && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Phone
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  <a
                    href={`tel:${organization.primary_contact_phone}`}
                    className='text-primary hover:underline'
                  >
                    {organization.primary_contact_phone}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Locations */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <MapPin className='w-5 h-5' />
            Locations ({organization.locations?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!organization.locations || organization.locations.length === 0 ? (
            <p className='text-gray-600 dark:text-gray-400 text-center py-4'>
              No locations added
            </p>
          ) : (
            <div className='max-h-[600px] overflow-y-auto pr-2'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-fr'>
                {organization.locations.map((location) => (
                  <div
                    key={location.id}
                    className='p-4 border border-gray-200 dark:border-gray-700 rounded-lg h-full'
                  >
                    {/* Location Logo and Name */}
                    <div className='flex items-start gap-3'>
                      {location.logo_url ? (
                        <div className='w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                          <img
                            src={location.logo_url}
                            alt={`${location.name} logo`}
                            className='w-full h-full object-cover'
                          />
                        </div>
                      ) : (
                        <div className='w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center shrink-0'>
                          <ImageIcon className='w-6 h-6 text-gray-400 dark:text-gray-500' />
                        </div>
                      )}
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-gray-900 dark:text-gray-100'>
                          {location.name}
                        </p>
                        {location.description && (
                          <p className='text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2'>
                            {location.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Actions */}
      {organization.status === 'PENDING_REVIEW' && (
        <ReviewActions organization={organization} />
      )}

      {/* Review History (if reviewed) */}
      {organization.reviewed_at && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center gap-2'>
              <FileText className='w-5 h-5' />
              Review History
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div>
              <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                Reviewed At
              </label>
              <p className='text-gray-900 dark:text-gray-100'>
                {formatDate(organization.reviewed_at)}
              </p>
            </div>

            {organization.review_notes && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Review Notes
                </label>
                <p className='text-gray-900 dark:text-gray-100'>
                  {organization.review_notes}
                </p>
              </div>
            )}

            {organization.rejection_reason && (
              <div>
                <label className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                  Rejection Reason
                </label>
                <p className='text-red-600 dark:text-red-400'>
                  {organization.rejection_reason}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default ReviewDetailPage;
